import React, { useState, useEffect } from "react";

interface UserProfile {
  id: number;
  name: string;
  phone: string;
  role: string;
  is_active: boolean;
  created_at?: string;
}

interface AttendanceStat {
  kpi_percentage?: number;
  total_days?: number;
  present_days?: number;
}

const ROLE_INFO: Record<string, { label: string; emoji: string; color: string; desc: string }> = {
  super_admin:      { label: "Super Admin",      emoji: "⚡", color: "#ffd6a5", desc: "Full system access, all modules" },
  owner:            { label: "Owner",            emoji: "👑", color: "#ffd6a5", desc: "Company owner, full access" },
  general_manager:  { label: "General Manager",  emoji: "🏛️", color: "#c8bfff", desc: "Operations, team & reports" },
  tech_shareholder: { label: "Tech Shareholder", emoji: "⚙️", color: "#c8bfff", desc: "Tech and operations access" },
  shareholder:      { label: "Shareholder",      emoji: "💼", color: "#c8bfff", desc: "Reports and finance access" },
  consultant:       { label: "Consultant",       emoji: "🤝", color: "#b8d58d", desc: "Reports access" },
  accountant:       { label: "Accountant",       emoji: "📊", color: "#b8d58d", desc: "Finance and accounting" },
  labor:            { label: "Operations",       emoji: "🔧", color: "#b8d58d", desc: "Operational tasks" },
  employee:         { label: "Employee",         emoji: "👤", color: "#b8d58d", desc: "Standard employee access" },
};

const s: Record<string, React.CSSProperties> = {
  page: {
    padding: "24px",
    fontFamily: "'Space Grotesk', sans-serif",
    color: "#dae2fd",
    maxWidth: 800,
  },
  card: {
    background: "rgba(19,27,46,0.7)",
    backdropFilter: "blur(14px)",
    border: "1px solid rgba(78,69,59,0.2)",
    borderRadius: 14,
    padding: "22px",
    marginBottom: 20,
  },
};

const UserCabinet: React.FC = () => {
  const [profile, setProfile]         = useState<UserProfile | null>(null);
  const [stats, setStats]             = useState<AttendanceStat>({});
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState<"profile" | "stats" | "security">("profile");
  const [editName, setEditName]       = useState("");
  const [saving, setSaving]           = useState(false);
  const [saveMsg, setSaveMsg]         = useState("");

  useEffect(() => {
    Promise.all([
      fetch("/api/auth/me", { credentials: "include" }).then(r => r.json()),
      fetch("/api/employee/my-dashboard", { credentials: "include" }).then(r => r.json()).catch(() => ({})),
    ]).then(([me, dash]) => {
      if (me.authenticated) {
        const p: UserProfile = {
          id: me.id || 0,
          name: me.name,
          phone: me.phone || "",
          role: me.role,
          is_active: true,
          created_at: me.created_at,
        };
        setProfile(p);
        setEditName(p.name);
        setStats({
          kpi_percentage: dash?.kpi_percentage,
          total_days: dash?.total_days,
          present_days: dash?.present_days,
        });
      }
    }).finally(() => setLoading(false));
  }, []);

  const roleInfo = profile ? (ROLE_INFO[profile.role] || ROLE_INFO.employee) : ROLE_INFO.employee;

  if (loading) {
    return (
      <div style={{ ...s.page, color: "#4a6070", fontSize: 13 }}>Loading your profile…</div>
    );
  }

  if (!profile) {
    return (
      <div style={{ ...s.page, color: "#ff6b6b", fontSize: 13 }}>Could not load profile. Please log in again.</div>
    );
  }

  const TABS = [
    { id: "profile",  label: "Profile",  icon: "👤" },
    { id: "stats",    label: "My Stats", icon: "📊" },
    { id: "security", label: "Security", icon: "🔐" },
  ] as const;

  return (
    <div style={s.page}>
      {/* Hero card */}
      <div style={{
        ...s.card,
        background: "linear-gradient(135deg, rgba(19,27,46,0.9), rgba(30,20,50,0.8))",
        border: `1px solid ${roleInfo.color}25`,
        display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
      }}>
        {/* Avatar */}
        <div style={{
          width: 72, height: 72,
          background: `linear-gradient(135deg, ${roleInfo.color}30, ${roleInfo.color}10)`,
          border: `2px solid ${roleInfo.color}40`,
          borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 32, flexShrink: 0,
        }}>
          {roleInfo.emoji}
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800, fontFamily: "'Manrope', sans-serif", color: "#dae2fd" }}>
            {profile.name}
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{
              background: `${roleInfo.color}15`,
              border: `1px solid ${roleInfo.color}30`,
              borderRadius: 6, padding: "3px 10px",
              fontSize: 11, fontWeight: 700, color: roleInfo.color,
              letterSpacing: "0.06em", textTransform: "uppercase",
            }}>{roleInfo.label}</span>
            <span style={{ fontSize: 12, color: "#4a6070", fontFamily: "monospace" }}>+{profile.phone}</span>
            <span style={{
              background: "rgba(184,213,141,0.12)",
              border: "1px solid rgba(184,213,141,0.25)",
              borderRadius: 6, padding: "3px 10px",
              fontSize: 10, fontWeight: 700, color: "#b8d58d",
            }}>ACTIVE</span>
          </div>
          <p style={{ margin: "6px 0 0", fontSize: 12, color: "#3a5060" }}>{roleInfo.desc}</p>
        </div>
        {/* KPI ring */}
        {stats.kpi_percentage !== undefined && (
          <div style={{ textAlign: "center", padding: "0 10px" }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: `conic-gradient(#b8d58d ${stats.kpi_percentage * 3.6}deg, rgba(255,255,255,0.05) 0deg)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 16px rgba(184,213,141,0.2)",
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: "50%",
                background: "rgba(11,19,38,0.95)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 800, color: "#b8d58d",
              }}>
                {stats.kpi_percentage?.toFixed(0)}%
              </div>
            </div>
            <div style={{ fontSize: 10, color: "#3a5060", marginTop: 4 }}>KPI Score</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "8px 18px",
              background: tab === t.id ? "rgba(200,191,255,0.15)" : "transparent",
              border: tab === t.id ? "1px solid rgba(200,191,255,0.3)" : "1px solid rgba(78,69,59,0.2)",
              borderRadius: 9,
              cursor: "pointer",
              color: tab === t.id ? "#c8bfff" : "#6a8090",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 12, fontWeight: 600,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Profile Tab ── */}
      {tab === "profile" && (
        <div style={s.card}>
          <h3 style={{ margin: "0 0 18px", fontSize: 14, color: "#c8bfff" }}>Profile Details</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { label: "Full Name",   value: profile.name,  editable: true, field: "name" },
              { label: "Phone",       value: `+${profile.phone}`, editable: false },
              { label: "Role",        value: roleInfo.label, editable: false },
              { label: "Status",      value: "Active", editable: false },
              { label: "Member Since", value: profile.created_at ? new Date(profile.created_at).toLocaleDateString("en-AE", { year: "numeric", month: "long" }) : "—", editable: false },
            ].map(item => (
              <div key={item.label}>
                <label style={{ display: "block", fontSize: 10, color: "#3a5060", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                  {item.label}
                </label>
                {item.editable && item.field === "name" ? (
                  <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    style={{
                      width: "100%",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(200,191,255,0.2)",
                      borderRadius: 8,
                      padding: "8px 12px",
                      color: "#dae2fd",
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: 13,
                      boxSizing: "border-box",
                    }}
                  />
                ) : (
                  <div style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(78,69,59,0.15)",
                    borderRadius: 8, padding: "8px 12px",
                    fontSize: 13, color: "#9ab0c9",
                  }}>{item.value}</div>
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20, display: "flex", gap: 10, alignItems: "center" }}>
            <button
              onClick={async () => {
                if (editName === profile.name) return;
                setSaving(true);
                // Would call PATCH /api/employees/{id} in real impl
                await new Promise(r => setTimeout(r, 600));
                setProfile(p => p ? { ...p, name: editName } : p);
                setSaving(false);
                setSaveMsg("Saved!");
                setTimeout(() => setSaveMsg(""), 2500);
              }}
              style={{
                background: "rgba(184,213,141,0.15)",
                border: "1px solid rgba(184,213,141,0.3)",
                borderRadius: 9, padding: "9px 22px",
                cursor: "pointer", color: "#b8d58d",
                fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 700,
              }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
            {saveMsg && <span style={{ color: "#b8d58d", fontSize: 12 }}>✅ {saveMsg}</span>}
          </div>
        </div>
      )}

      {/* ── Stats Tab ── */}
      {tab === "stats" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
            {[
              { label: "KPI Score",     value: stats.kpi_percentage !== undefined ? `${stats.kpi_percentage?.toFixed(1)}%` : "—", icon: "🎯", color: "#b8d58d" },
              { label: "Days Present",  value: stats.present_days?.toString() || "—", icon: "✅", color: "#c8bfff" },
              { label: "Total Days",    value: stats.total_days?.toString() || "—",   icon: "📅", color: "#ffd6a5" },
            ].map(stat => (
              <div key={stat.label} style={{
                ...s.card, marginBottom: 0, textAlign: "center",
                border: `1px solid ${stat.color}20`,
              }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{stat.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: stat.color, fontFamily: "'Manrope', sans-serif" }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 11, color: "#3a5060", marginTop: 4 }}>{stat.label}</div>
              </div>
            ))}
          </div>
          <div style={s.card}>
            <p style={{ margin: 0, fontSize: 12, color: "#3a5060" }}>
              View full attendance history in the <a href="/attendance" style={{ color: "#c8bfff" }}>Attendance Dashboard</a>.
              Voice call history is in <a href="/voice-sales" style={{ color: "#c8bfff" }}>Voice Sales</a>.
            </p>
          </div>
        </div>
      )}

      {/* ── Security Tab ── */}
      {tab === "security" && (
        <div style={s.card}>
          <h3 style={{ margin: "0 0 18px", fontSize: 14, color: "#c8bfff" }}>Security & Access</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              { label: "Authentication",    value: "WhatsApp OTP",    icon: "📱", status: "Active",   color: "#b8d58d" },
              { label: "Session",           value: "Cookie-based",    icon: "🍪", status: "Valid",    color: "#b8d58d" },
              { label: "2FA",               value: "via WhatsApp",    icon: "🔐", status: "Enabled",  color: "#b8d58d" },
              { label: "Last Login",        value: "Today, Dubai GST", icon: "🕐", status: "",         color: "#4a6070" },
            ].map(item => (
              <div key={item.label} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 0", borderBottom: "1px solid rgba(78,69,59,0.1)",
              }}>
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: "#9ab0c9" }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: "#3a5060" }}>{item.value}</div>
                </div>
                {item.status && (
                  <span style={{
                    background: `${item.color}15`, border: `1px solid ${item.color}30`,
                    borderRadius: 6, padding: "2px 9px",
                    fontSize: 10, fontWeight: 700, color: item.color,
                  }}>{item.status}</span>
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20 }}>
            <button
              onClick={() => fetch("/api/auth/logout", { method: "POST", credentials: "include" }).then(() => window.location.reload())}
              style={{
                background: "rgba(255,107,107,0.1)",
                border: "1px solid rgba(255,107,107,0.25)",
                borderRadius: 9, padding: "9px 22px",
                cursor: "pointer", color: "#ff6b6b",
                fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 700,
              }}
            >
              🚪 Sign Out All Sessions
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCabinet;
